import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

DateRange.propTypes = {
  title: PropTypes.string
};

export default function DateRange({ title }) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setStartDate(null);
    setEndDate(null);

    const start = searchParams.get('start');
    const end = searchParams.get('end');

    if (start && end) {
      setStartDate(
        new Date(start).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        })
      );

      setEndDate(
        new Date(end).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        })
      );
    }
  }, [searchParams]);

  // TODO: Colocar essa função no helper
  const updateSearchParams = (key, value, remove) => {
    const params = new URL(document.location).searchParams;

    const updatedSearchParams = new URLSearchParams(params.toString());

    if (remove) {
      updatedSearchParams.delete(key);
    } else {
      updatedSearchParams.set(key, value);
    }
    return updatedSearchParams.toString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <DatePicker
          label="Start"
          value={startDate || null}
          showToolbar={false}
          actionBar={false}
          onChange={(newStartDate) => {
            setStartDate(newStartDate);

            if (newStartDate?.isValid() && endDate && dayjs(endDate).isValid()) {
              const formattedStartDate = newStartDate
                .set('hour', 0)
                .set('minute', 0)
                .set('second', 0)
                .set('millisecond', 0)
                .tz('Europe/Lisbon', true)
                .toISOString();

              const formattedEndDate = dayjs(endDate)
                .set('hour', 23)
                .set('minute', 59)
                .set('second', 59)
                .set('millisecond', 999)
                .tz('Europe/Lisbon', true)
                .toISOString();

              setSearchParams(updateSearchParams('start', formattedStartDate));
              setSearchParams(updateSearchParams('end', formattedEndDate));
            } else if (newStartDate === null && !endDate) {
              setSearchParams(updateSearchParams('start', '', true));
              setSearchParams(updateSearchParams('end', '', true));
            }
          }}
          componentsProps={{
            actionBar: {
              actions: ['cancel']
            }
          }}
          renderInput={(params) => (
            <TextField fullWidth helperText={params?.inputProps?.placeholder} {...params} />
          )}
        />
        <DatePicker
          label="End"
          value={endDate || null}
          showToolbar={false}
          actionBar={false}
          onChange={(newEndDate) => {
            setEndDate(newEndDate);

            if (newEndDate?.isValid() && startDate && dayjs(startDate).isValid()) {
              const formattedStartDate = dayjs(startDate)
                .set('hour', 0)
                .set('minute', 0)
                .set('second', 0)
                .set('millisecond', 0)
                .tz('Europe/Lisbon', true)
                .toISOString();

              const formattedEndDate = newEndDate
                .set('hour', 23)
                .set('minute', 59)
                .set('second', 59)
                .set('millisecond', 999)
                .tz('Europe/Lisbon', true)
                .toISOString();

              setSearchParams(updateSearchParams('start', formattedStartDate));
              setSearchParams(updateSearchParams('end', formattedEndDate));
            } else if (newEndDate === null && !startDate) {
              setSearchParams(updateSearchParams('start', '', true));
              setSearchParams(updateSearchParams('end', '', true));
            }
          }}
          componentsProps={{
            actionBar: {
              actions: ['cancel']
            }
          }}
          renderInput={(params) => (
            <TextField fullWidth helperText={params?.inputProps?.placeholder} {...params} />
          )}
        />
      </Stack>
    </LocalizationProvider>
  );
}
